import { useMemo } from 'react';

import LinkIcon from '@mui/icons-material/Link';
import type { ColDef, ValueGetterParams, ValueSetterParams } from 'ag-grid-community';
import type { TFunction } from 'i18next';
import { renderToString } from 'react-dom/server';
import { useRecoilValue } from 'recoil';
import { omit, pick } from 'remeda';

import {
  LocationEditor,
  ResolutionLevelEditor,
  StatusEditor,
} from '../../../../../components/IncidentTable/components/editor';
import { LocationRenderer, ResolutionLevelRenderer } from '../../../../../components/IncidentTable/components/renderer';
import { RelationRenderer } from '../../../../../components/IncidentTable/components/renderer/RelationRenderer';
import {
  titleField as incidentTitleField,
  descriptionField as incidentDescriptionField,
  responsibleField as incidentResponsible,
  userFunctionField as incidentUserFunctionField,
  statusByField,
  commentsField as incidentCommentsField,
} from '../../../../../components/IncidentTable/hooks';
import type { CellPermissions } from '../../../../../components/IncidentTable/utils/cellCanBeEdited';
import getHeaderTemplate from '../../../../../components/IncidentTable/utils/getHeaderTemplate';
import { DATETIME_FORMAT } from '../../../../../constants/base';
import { currentEventSettings, permissionState } from '../../../../../state/event';
import { type User } from '../../../../../types';
import {
  cellTypes,
  dateTimeField,
  defaultCellTypeFns,
  multiUsersGridField,
  userFunctionTypeFns,
} from '../../../../EntitiesGrid';
import { defaultCellStyles } from '../../../../EntitiesGrid/lib';
import {
  comparatorLocation,
  comparatorRelation,
  comparatorResolutionLevel,
  comparatorUserFunction,
} from '../../../lib/comparators';
import { getResolutionLevelForChildrenNodes } from '../../../lib/resolutionLevelForChildrenNodes';
import { newRowDailyTask, type DailyTask } from '../../../state/dailyTasks';
import type { ProjectTask } from '../../../state/projectTasks';
import { ProjectTaskColumnKey } from '../../../state/projectTasks';
import { NON_EDITABLE_FIELDS, NON_EDITABLE_ON_ADD_FIELDS, isPreparedNewTask } from '../../../state/tasks';
import { editableStatus } from './editable/editableStatus';
import { getStatusForCellStatus } from './getStatusForCellStatus';
import { useEditable } from './useEditable';

export const relationField = (): ColDef<DailyTask> => ({
  headerComponentParams: {
    template: getHeaderTemplate({
      contentTemplate: renderToString(<LinkIcon htmlColor='rgba(0, 0, 0, 0.54)' viewBox='0 0 26 26' />),
    }),
  },
  field: 'relation',
  width: 70,
  resizable: false,
  editable: false,
  cellRenderer: RelationRenderer,
  valueGetter: (params) => params.data?.relation,
  comparator: comparatorRelation,
  sortable: true,
});

const taskDateTimeField = (cellEditorParams: Record<string, unknown>) =>
  dateTimeField({
    cellEditorParams,
    sortable: true,
  });

export const startDatetimeField = (t: TFunction<'translation'>, showRequired = false) => ({
  ...taskDateTimeField({
    maxDateTime: 'endDatetime',
    inputFormat: DATETIME_FORMAT.full,
  }),
  headerName: showRequired ? t('start-datetime-required') : t('start-datetime'),
  field: 'startDatetime',
});

export const endDatetimeField = (t: TFunction<'translation'>): ColDef => ({
  ...taskDateTimeField({
    minDateTime: 'startDatetime',
    inputFormat: DATETIME_FORMAT.full,
  }),
  headerName: t('end-datetime'),
  field: 'endDatetime',
});

export const titleField = (t: TFunction<'translation'>, showRequired = false) => ({
  headerName: showRequired ? t('title-required') : t('title'),
  field: 'title',
  ...pick(incidentTitleField(t), [
    'cellRenderer',
    'cellClass',
    'cellEditorPopup',
    'cellEditor',
    'width',
    'suppressKeyboardEvent',
    'flex',
  ]),
  sortable: true,
  ...defaultCellTypeFns<string, ProjectTask>(cellTypes.stringType),
  cellEditorParams: {
    inputProps: {
      maxLength: 255,
    },
  },
});

export const descriptionField = (t: TFunction<'translation'>) => ({
  headerName: t('description'),
  field: 'description',
  ...pick(incidentDescriptionField(t), [
    'cellRenderer',
    'cellEditorPopup',
    'cellEditor',
    'minWidth',
    'suppressKeyboardEvent',
    'flex',
  ]),
  sortable: true,
  ...defaultCellTypeFns<string, ProjectTask>(cellTypes.stringType),
});

export const responsibleField = (t: TFunction<'translation'>) => ({
  headerName: t('resp.'),
  field: 'responsible',
  ...pick(incidentResponsible(t), [
    'cellRenderer',
    'cellEditor',
    'equals',
    'cellEditorPopup',
    'resizable',
    'singleClickEdit',
    'width',
    'minWidth',
  ]),
  sortable: true,
  ...defaultCellTypeFns<User, ProjectTask>(cellTypes.userType),
});

export const userFunctionField = (t: TFunction<'translation'>, showRequired = false): ColDef => ({
  headerName: showRequired ? t('function-required') : t('function'),
  field: 'userFunction',
  valueGetter: (params: ValueGetterParams) => params.data.userFunction?.name,
  ...pick(incidentUserFunctionField(t), ['cellRenderer', 'cellEditor', 'cellEditorPopup', 'singleClickEdit', 'width']),
  sortable: true,
  ...userFunctionTypeFns<ProjectTask>(),
  comparator: comparatorUserFunction,
});

export const statusField = (
  t: TFunction<'translation'>,
  valueSetter: (params: ValueSetterParams) => boolean,
  permissions: CellPermissions['permissions'],
): ColDef => ({
  ...statusByField(t, valueSetter, permissions),
  headerName: t('status'),
  field: 'status',
  comparator: cellTypes.statusType.comparator,
  sortable: true,
  cellEditorSelector: (params) => ({
    component: StatusEditor,
    params: {
      ...getStatusForCellStatus(params, permissions),
    },
  }),
  editable: editableStatus,
});

export const commentsField = (
  t: TFunction<'translation'>,
  valueSetter: (params: ValueSetterParams) => boolean,
  permissions: CellPermissions['permissions'],
): ColDef => ({
  ...omit(incidentCommentsField(t, valueSetter, permissions), ['editable']),
  headerName: t('comments'),
  field: 'comments',
});

export const resolvedDateField = (t: TFunction<'translation'>) => ({
  ...dateTimeField({
    editable: false,
    sortable: true,
  }),
  headerName: t('resolved'),
  field: 'resolvedDate',
});

export const participantsField = (t: TFunction<'translation'>) => ({
  ...multiUsersGridField({ editable: true, sortable: true }),
  headerName: t('participants'),
  field: ProjectTaskColumnKey.Participants,
});

export const resolutionLevelField = (t: TFunction<'translation'>): ColDef => ({
  headerName: t('level'),
  field: 'resolutionLevel',
  cellEditorPopup: true,
  cellEditorSelector: (params) => {
    const levels = getResolutionLevelForChildrenNodes(params.node);
    return {
      component: ResolutionLevelEditor,
      params: {
        levels,
      },
    };
  },
  width: 84,
  resizable: false,
  cellRenderer: ResolutionLevelRenderer,
  singleClickEdit: true,
  sortable: true,
  comparator: comparatorResolutionLevel,
});

export const locationField = (t: TFunction<'translation'>): ColDef => ({
  headerName: t('location'),
  field: 'location',
  cellRenderer: LocationRenderer,
  cellEditor: LocationEditor,
  cellEditorPopup: true,
  singleClickEdit: true,
  width: 125,
  sortable: true,
  comparator: comparatorLocation,
});

export function useColumnDefs(
  t: TFunction<'translation'>,
  statusSetter: (params: ValueSetterParams) => boolean,
  commentsSetter: (params: ValueSetterParams) => boolean,
  defaultValueSetter: ({ colDef, data, newValue }: ValueSetterParams<unknown>) => boolean,
) {
  const defaultSettings = useMemo<ColDef>(
    () => ({
      valueSetter: defaultValueSetter,
      comparator: () => 0,
    }),
    [defaultValueSetter],
  );
  const {
    canUpdateResolvedIncident,
    canUpdateIncident,
    canUpdateAllIncidents,
    canUpdateResolvedIncidentComment,
    canCreateComment,
  } = useRecoilValue(permissionState);
  const eventSettings = useRecoilValue(currentEventSettings);
  const newEntity = useRecoilValue(newRowDailyTask);
  const isNewEntity = Boolean(newEntity);

  const columnDefs = useMemo<ColDef[]>(
    () =>
      [
        ...(eventSettings.availableIncidentPlan ? [relationField()] : []),
        startDatetimeField(t, isNewEntity),
        endDatetimeField(t),
        titleField(t, isNewEntity),
        descriptionField(t),
        userFunctionField(t, isNewEntity),
        locationField(t),
        responsibleField(t),
        statusField(t, statusSetter, {
          canUpdateResolvedIncident,
          canUpdateIncident,
          canUpdateAllIncidents,
          canUpdateResolvedIncidentComment,
          canCreateComment,
        }),
        resolutionLevelField(t),
        commentsField(t, commentsSetter, {
          canUpdateResolvedIncident,
          canUpdateIncident,
          canUpdateAllIncidents,
          canUpdateResolvedIncidentComment,
          canCreateComment,
        }),
        resolvedDateField(t),
        participantsField(t),
      ].map((colDef) => ({ ...defaultSettings, ...colDef })),
    [
      eventSettings.availableIncidentPlan,
      t,
      isNewEntity,
      statusSetter,
      canUpdateResolvedIncident,
      canUpdateIncident,
      canUpdateAllIncidents,
      canUpdateResolvedIncidentComment,
      canCreateComment,
      commentsSetter,
      defaultSettings,
    ],
  );

  return columnDefs;
}

export function useDefaultColDef() {
  const { canUpdateAllOperationalTask, canUpdateOperationalTask, canUpdateResolvedOperationalTask, canCreateComment } =
    useRecoilValue(permissionState);
  const { editable } = useEditable({
    isPreparedEntity: isPreparedNewTask,
    nonEditableFields: NON_EDITABLE_FIELDS,
    nonEditableOnPreparedFields: NON_EDITABLE_ON_ADD_FIELDS,
    permissions: {
      canUpdateAllTask: canUpdateAllOperationalTask,
      canUpdateResolvedTasks: canUpdateResolvedOperationalTask,
      canUpdateTask: canUpdateOperationalTask,
      canCreateComment,
    },
  });
  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: false,
      hide: false,
      resizable: true,
      editable,
      minWidth: 60,
      enableCellChangeFlash: true,
      cellStyle: defaultCellStyles,
      suppressMenu: true,
    }),
    [editable],
  );

  return defaultColDef;
}
